import { useEffect, useState } from "react";
import { Download, Smartphone, Share, X } from "lucide-react";

const DISMISS_KEY = "tb_install_dismissed";

function isStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}
function isIOS() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent);
}

export default function InstallBanner() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [help, setHelp] = useState(false);

  useEffect(() => {
    if (isStandalone() || localStorage.getItem(DISMISS_KEY)) return;

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => {
      setVisible(false);
      localStorage.setItem(DISMISS_KEY, "1");
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);

    const t = setTimeout(() => setVisible(true), 4000);
    return () => {
      clearTimeout(t);
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  // Show only when we have a real prompt (Android/desktop) or on iOS (fallback).
  const canShow = visible && (deferred || isIOS());
  if (!canShow) return null;

  const dismiss = () => {
    setVisible(false);
    localStorage.setItem(DISMISS_KEY, "1");
  };

  const install = async () => {
    if (deferred) {
      deferred.prompt();
      try {
        await deferred.userChoice;
      } catch {}
      setDeferred(null);
      dismiss();
    } else {
      setHelp(true);
    }
  };

  return (
    <>
      <div
        className="fixed bottom-20 md:bottom-4 inset-x-3 md:inset-x-auto md:right-4 md:w-96 z-50 animate-fade-up"
        data-testid="install-banner"
      >
        <div className="relative flex items-center gap-3 bg-[#0F172A] text-white rounded-2xl shadow-2xl px-4 py-3">
          <div className="w-11 h-11 rounded-xl bg-[#C85A32] flex items-center justify-center shrink-0 font-display font-black text-lg">
            T
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-display font-bold text-[15px] leading-tight">Installer TOGOBATI</p>
            <p className="text-xs text-slate-300 mt-0.5">Accès rapide, même en connexion faible.</p>
          </div>
          <button
            onClick={install}
            className="inline-flex items-center gap-1.5 bg-[#C85A32] hover:bg-[#A94520] text-white text-sm font-semibold px-3 py-2 rounded-full transition shrink-0"
            data-testid="install-banner-btn"
          >
            <Download className="w-4 h-4" />
            Installer
          </button>
          <button
            onClick={dismiss}
            className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-slate-700 hover:bg-slate-600 flex items-center justify-center"
            data-testid="install-banner-close"
            aria-label="Fermer"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {help && (
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="install-help">
          <div className="absolute inset-0 bg-black/50" onClick={() => setHelp(false)} />
          <div className="relative bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-md p-6 animate-fade-up max-h-[85vh] overflow-y-auto">
            <button onClick={() => setHelp(false)} className="absolute top-4 right-4 text-slate-400" data-testid="install-help-close">
              <X className="w-5 h-5" />
            </button>
            <div className="w-12 h-12 rounded-xl bg-[#FDF3EE] flex items-center justify-center mb-3">
              <Smartphone className="w-6 h-6 text-[#C85A32]" />
            </div>
            <h2 className="font-display text-2xl font-bold text-[#0F172A]">Installer TOGOBATI</h2>
            <p className="text-sm text-slate-500 mt-1 mb-4">Ajoutez l'application à votre écran d'accueil pour un accès rapide, même avec une connexion faible.</p>
            <ol className="space-y-2 text-sm text-slate-700 list-decimal list-inside">
              <li>Appuyez sur le bouton <Share className="inline w-4 h-4 -mt-0.5" /> <strong>Partager</strong> dans Safari.</li>
              <li>Choisissez <strong>« Sur l'écran d'accueil »</strong>.</li>
              <li>Appuyez sur <strong>Ajouter</strong> — c'est prêt !</li>
            </ol>
          </div>
        </div>
      )}
    </>
  );
}
